import React from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';
import { 
  FileTextIcon, 
  ShieldIcon, 
  AlertTriangleIcon,
  ClockIcon, 
  HelpCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  GraduationCapIcon
} from 'lucide-react';

const PLAN_TEMPLATES = gql`
  query PlanTemplates {
    getCurrentUser {
      id
      company {
        id
        features
      }
    }
    planTemplates {
      id
      name
      slug
      planType
      description
      estimatedMinutesMin
      estimatedMinutesMax
      icon
      ceEligible
      ceCreditHours
      planModules {
        id
        title
        planSections {
          id
          planQuestions {
            id
          }
        }
      }
    }
  }
`;

// Badge component for plan type
const TypeBadge = ({ type }) => {
  const intl = useIntl();
  const typeConfig = {
    succession: {
      label: intl.formatMessage({ id: 'succession.plans.type_succession' }),
      classes: "bg-blue-500/10 text-blue-700 border-blue-500/20"
    },
    bcp: {
      label: intl.formatMessage({ id: 'succession.plans.type_bcp' }),
      classes: "bg-green-500/10 text-green-700 border-green-500/20"
    },
    catastrophic: {
      label: intl.formatMessage({ id: 'succession.plans.type_catastrophic' }),
      classes: "bg-orange-500/10 text-orange-700 border-orange-500/20"
    }
  };

  const config = typeConfig[type] || typeConfig.succession;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.classes}`}>
      {config.label}
    </span>
  );
};

// Icon mapping based on plan type
const getIconForType = (type) => {
  switch (type) {
    case 'bcp':
      return ShieldIcon;
    case 'catastrophic':
      return AlertTriangleIcon;
    default:
      return FileTextIcon;
  }
};

// Get features/modules for a template (fallback strings are intentionally not i18n'd here
// as they are only shown when the server has no module data; the real data comes from DB)
const getFeatures = (template) => {
  if (template.planModules && template.planModules.length > 0) {
    return template.planModules.slice(0, 4).map(m => m.title);
  }
  // Fallback features based on type (DB-driven in production, these are last-resort placeholders)
  const fallbackFeatures = {
    succession: [
      "Foundation & Vision",
      "Financial & Legal Framework",
      "Execution & Transition",
      "Client Communication Strategy"
    ],
    bcp: [
      "Risk Assessment",
      "Emergency Procedures",
      "Communication Protocols",
      "Recovery Strategies"
    ],
    catastrophic: [
      "Emergency Contacts",
      "Asset Documentation",
      "Client Transition",
      "Legal Directives"
    ]
  };
  return fallbackFeatures[template.planType] || fallbackFeatures.succession;
};

// Calculate total questions
const getTotalQuestions = (template) => {
  if (!template.planModules) return 0;
  return template.planModules.reduce((modAcc, mod) => {
    return modAcc + (mod.planSections?.reduce((secAcc, sec) => {
      return secAcc + (sec.planQuestions?.length || 0);
    }, 0) || 0);
  }, 0);
};

const TemplateCard = ({ template, isRecommended = false, ceCredit = null, continuityBuilderEnabled = false }) => {
  const intl = useIntl();
  const Icon = getIconForType(template.planType);
  const features = getFeatures(template);
  const totalQuestions = getTotalQuestions(template);

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow relative flex flex-col h-full">
      {isRecommended && (
        <span className="absolute top-4 right-4 bg-succession-primary text-white text-xs font-medium px-2.5 py-1 rounded">
          {intl.formatMessage({ id: 'succession.plans.recommended_badge' })}
        </span>
      )}

      <div className="space-y-4 flex-1 flex flex-col">
        {/* Icon & Title */}
        <div className="space-y-3">
          <div className="w-12 h-12 bg-succession-primary/10 rounded-lg flex items-center justify-center">
            <Icon className="w-6 h-6 text-succession-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
              {template.name}
            </h3>
            <TypeBadge type={template.planType} />
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-succession-text-secondary flex-1">
          {template.description || intl.formatMessage({ id: 'succession.plans.template_default_description' })}
        </p>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-succession-text-muted">
          <div className="flex items-center gap-1">
            <ClockIcon className="w-3 h-3" />
            {intl.formatMessage({ id: 'succession.plans.template_minutes_range' }, { min: template.estimatedMinutesMin, max: template.estimatedMinutesMax })}
          </div>
          <div className="flex items-center gap-1">
            <HelpCircleIcon className="w-3 h-3" />
            {intl.formatMessage({ id: 'succession.plans.template_questions_count' }, { count: totalQuestions })}
          </div>
        </div>

        {/* Features */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-succession-text-muted">
            {intl.formatMessage({ id: 'succession.plans.whats_included' })}
          </p>
          <ul className="space-y-1">
            {features.map((feature, idx) => (
              <li
                key={idx}
                className="text-xs text-succession-text-secondary flex items-start gap-2"
              >
                <span className="text-succession-primary mt-0.5">•</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CE Credit Indicator */}
        {ceCredit?.available && (
          <div className="flex items-center gap-2 text-xs bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-300 border border-green-500/20 rounded-md px-3 py-2">
            <GraduationCapIcon className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              {intl.formatMessage({ id: 'succession.plans.ce_credit_upon_completion' }, { hours: ceCredit.creditHours })}
            </span>
          </div>
        )}

        {/* CTA */}
        <Button
          href={continuityBuilderEnabled && template.planType === 'catastrophic' ? '/succession/pact-builder' : `/succession/plans/${template.slug}`}
          as="a"
          variant="primary"
          className="w-full justify-center mt-auto"
        >
          {intl.formatMessage({ id: 'succession.plans.start_plan' })}
          <ArrowRightIcon className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );
};

const HelpSection = () => {
  const intl = useIntl();
  return (
    <Card className="p-6 bg-succession-bg-secondary/50">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-succession-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <HelpCircleIcon className="w-5 h-5 text-succession-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.plans.help_title' })}</h4>
          <p className="text-sm text-succession-text-secondary mb-4">
            {intl.formatMessage({ id: 'succession.plans.help_subtitle' })}
          </p>
          <ul className="space-y-2 text-sm text-succession-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-succession-primary mt-0.5">•</span>
              <span>
                <strong className="text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.help_succession_label' })}:</strong>{" "}
                {intl.formatMessage({ id: 'succession.plans.help_succession_description' })}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-succession-primary mt-0.5">•</span>
              <span>
                <strong className="text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.help_bcp_label' })}:</strong>{" "}
                {intl.formatMessage({ id: 'succession.plans.help_bcp_description' })}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-succession-primary mt-0.5">•</span>
              <span>
                <strong className="text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.help_catastrophic_label' })}:</strong>{" "}
                {intl.formatMessage({ id: 'succession.plans.help_catastrophic_description' })}
              </span>
            </li>
          </ul>
          <p className="text-sm text-succession-text-secondary mt-4">
            {intl.formatMessage({ id: 'succession.plans.help_footer' })}
          </p>
        </div>
      </div>
    </Card>
  );
};

const NewSuccessionPlan = () => {
  const intl = useIntl();
  const { loading, error, data } = useQuery(PLAN_TEMPLATES);

  const backButton = (
    <Button href="/succession/plans" as="a" variant="ghost" size="sm">
      <ArrowLeftIcon className="w-4 h-4 mr-2" />
      {intl.formatMessage({ id: 'succession.plans.back_to_plans' })}
    </Button>
  );

  if (loading) {
    return (
      <SingleColumnTemplate
        title={intl.formatMessage({ id: 'succession.plans.create_new_title' })}
        subtitle={intl.formatMessage({ id: 'succession.plans.create_new_subtitle' })}
        breadcrumbs={backButton}
      >
        <div className="p-12 text-center text-succession-text-muted">
          {intl.formatMessage({ id: 'succession.plans.loading_templates' })}
        </div>
      </SingleColumnTemplate>
    );
  }

  if (error) {
    return (
      <SingleColumnTemplate
        title={intl.formatMessage({ id: 'succession.plans.create_new_title' })}
        subtitle={intl.formatMessage({ id: 'succession.plans.create_new_subtitle' })}
        breadcrumbs={backButton}
      >
        <div className="p-12 text-center text-red-500">
          {intl.formatMessage({ id: 'succession.plans.error_loading_templates' }, { message: error.message })}
        </div>
      </SingleColumnTemplate>
    );
  }

  const templates = data?.planTemplates || [];
  const hasTemplates = templates.length > 0;
  
  // Check feature flags
  const currentUser = data?.getCurrentUser;
  const features = currentUser?.company?.features || [];
  const ceFeatureEnabled = features.includes('plan_builder_ce');
  const continuityBuilderEnabled = features.includes('continuity_builder');

  // Determine which template is "recommended" (first succession type, or first overall)
  const recommendedId = templates.find(t => t.planType === 'succession')?.id || templates[0]?.id;
  
  // Build CE credit info for each template
  const getCeCredit = (template) => {
    if (!ceFeatureEnabled || !template.ceEligible) return null;
    return {
      available: true,
      creditHours: template.ceCreditHours || 1,
    };
  };

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'succession.plans.create_new_title' })}
      subtitle={intl.formatMessage({ id: 'succession.plans.create_new_subtitle' })}
      breadcrumbs={backButton}
      variant="wide"
    >
      {!hasTemplates ? (
        // Empty State
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <div className="w-16 h-16 bg-succession-bg-secondary rounded-full flex items-center justify-center">
              <FileTextIcon className="w-8 h-8 text-succession-text-muted" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-succession-text-primary mb-2">
                {intl.formatMessage({ id: 'succession.plans.no_templates_title' })}
              </h3>
              <p className="text-succession-text-secondary max-w-md">
                {intl.formatMessage({ id: 'succession.plans.no_templates_description' })}
              </p>
            </div>
            <Button href="/succession/plans" as="a" variant="outline" className="mt-4">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.back_to_plans' })}
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* Template Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                isRecommended={template.id === recommendedId}
                ceCredit={getCeCredit(template)}
                continuityBuilderEnabled={continuityBuilderEnabled}
              />
            ))}
          </div>

          {/* Help Section */}
          <HelpSection />
        </>
      )}
    </SingleColumnTemplate>
  );
};

export default NewSuccessionPlan;
